/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { TRANSITION_TIMEOUT_TIME } from '../../Data/Constants';
import appointmentBookingConfig from '../../Data/Maps/appointmentBookingConfig';
import cpcCardConfig from '../../Data/Maps/cpcCardConfig';
import {
  updateEnterPhase,
  updateExitPhase,
} from '../../Lib/redux/slices/transition';
import {
  activateSynchronisedEnterAnimation,
  isThereConfigForFormId,
  prepareTransitionContainerForSynchronisedAnimation,
} from './transition.helpers';

const Transition = ({
  currentPage,
  isIn,
  children,
  direction,
  onContentMounted,
}) => {
  const nodeRef = useRef(null);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isDynamicHeightEnabled, widgetResizeDuration } = useSelector(
    (store) => store.optionalFormConfig
  );
  const { formId } = useSelector((store) => store.formConfig);
  const { wasPreviousPageCpcCard, showCpcRecommendations, isNextPageCpcCard } =
    useSelector((store) => store.cpcCard);
  const { wasPreviousPageAppointmentBooking, showAppointmentBooking } =
    useSelector((store) => store.appointmentBooking);
  const { requiredWidgetHeight, isWidgetHeightRecalculationRequired } =
    useSelector((store) => store.dynamicHeight);
  const [synchronisedAnimationPrepared, setSynchronisedAnimationPrepared] =
    useState(false);
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );

  /*
   * handleExitPhase
   *
   * This function sets state variables to report the beginning of
   * the exit phase and of the page transition as a whole to the wider app.
   *
   * isPageTransitionInProgress
   * Necessity for this new instant access boolean variable:
   * For components that get mounted as part of a page transition (its enter phase to be exact)
   * The store variables exitPhase & enterPhase update too slowly to reliably confirm an on-going
   * page transition. This is because they are both false for a brief timeframe during the
   * page transition flow, lagging behind the actual state of <Transition/> as described below:
   *
   * Page transition stages:
   * - EXIT PHASE starts (new var isPageTransitionInProgress gets set to true)
   * - EXIT animation begins
   * - exitPhase store variable is currently false
   * - exitPhase store variable gets updated to true
   * - EXIT animation ends
   * - component unmounts
   * - EXIT PHASE ends
   * - exitPhase store variable gets updated to false
   * - ENTER PHASE starts
   * ** PROBLEM **: - enterPhase store variable is currently false
   * ** PROBLEM **: - new component mounts (referring to the enterPhase would suggest
                      that there is no transition in progress, the new var fixes this)
   * - ENTER animation begins
   * - enterPhase store variable gets updated to true
   * - ENTER animation ends
   * - ENTER PHASE ends (new var isPageTransitionInProgress gets set to false)
   * - enterPhase store variable gets updated to false
   */
  const handleExitPhase = () => {
    if (
      !window.__private__ ||
      typeof window.__private__ !== 'object' ||
      Array.isArray(window.__private__)
    ) {
      window.__private__ = {};
    }
    window.__private__.isPageTransitionInProgress = true;
    // Inform the wider app of the beginning of the EXIT phase
    dispatch(
      updateExitPhase({
        exitPhase: true,
      })
    );
  };

  /**
   * prepareWidgetForEnterAnimation
   *
   * This function invokes CSS transition related setup functions and a transition
   * initiation function and sets a state variable to report the beginning of
   * the enter phase to the wider app.
   */
  const prepareWidgetForEnterAnimation = () => {
    const isCpcCardForm = isThereConfigForFormId({
      config: cpcCardConfig,
      formId,
    });

    const isAppointmentBookingForm = isThereConfigForFormId({
      config: appointmentBookingConfig,
      formId,
    });

    /**
     * prepareWidgetForEnterAnimation
     *
     * In order to achieve an enter animation of the inputArea that is synchronised with the widget height resize speed
     * it will not take place as part of the ENTER phase of this Transition component, but after that phase.
     * It gets accomplished as part of a custom transition that is managed by the activateSynchronisedEnterAnimation
     * helper function.
     */
    if (
      isDynamicHeightEnabled &&
      isWidgetHeightRecalculationRequired === false
    ) {
      window.__private__.isCustomPageTransitionRequired = true;
      prepareTransitionContainerForSynchronisedAnimation({
        direction,
        theme,
        mode: {
          cpcCardTransitionContainer: isCpcCardForm,
          appointmentBookingTransitionContainer: isAppointmentBookingForm,
          newsletterSignupTransitionContainer: false,
          transitionContainer: true,
        },
        dispatch,
        widgetResizeDuration,
        isBounceNavigationAnimationEnabled,
      });
      setSynchronisedAnimationPrepared(true);
    }

    if (!isDynamicHeightEnabled && isCpcCardForm) {
      window.__private__.isCustomPageTransitionRequired = true;
      prepareTransitionContainerForSynchronisedAnimation({
        direction,
        theme,
        mode: {
          cpcCardTransitionContainer: isCpcCardForm,
          newsletterSignupTransitionContainer: false,
          transitionContainer: true,
        },
        dispatch,
        widgetResizeDuration,
        isBounceNavigationAnimationEnabled,
      });

      activateSynchronisedEnterAnimation({
        widgetResizeDuration,
        mode: {
          cpcCardTransitionContainer: isNextPageCpcCard === true,
          transitionContainer: true,
        },
        isBounceNavigationAnimationEnabled,
      });
    }

    if (!isDynamicHeightEnabled && isAppointmentBookingForm) {
      window.__private__.isCustomPageTransitionRequired = true;
      prepareTransitionContainerForSynchronisedAnimation({
        direction,
        theme,
        mode: {
          appointmentBookingTransitionContainer: isAppointmentBookingForm,
          cpcCardTransitionContainer: false,
          newsletterSignupTransitionContainer: false,
          transitionContainer: true,
        },
        dispatch,
        widgetResizeDuration,
        isBounceNavigationAnimationEnabled,
      });

      activateSynchronisedEnterAnimation({
        widgetResizeDuration,
        mode: {
          transitionContainer: true,
          appointmentBookingTransitionContainer: true,
        },
        isBounceNavigationAnimationEnabled,
      });
    }

    // Inform the wider app of the beginning of the ENTER phase
    dispatch(
      updateEnterPhase({
        enterPhase: true,
      })
    );
  };

  useEffect(() => {
    /* This side effect handler kicks off the enterAnimation after the
     * dynamic height calculation for the next page is completed.
     * As soon as that is the case (whilst the synchronisedAnimationPrepared
     * state is true), we may kick off the enterAnimation.
     */
    const noChildIframe =
      (wasPreviousPageCpcCard === false && showCpcRecommendations === false) ||
      (wasPreviousPageAppointmentBooking === false &&
        showAppointmentBooking === false);

    if (
      isDynamicHeightEnabled &&
      synchronisedAnimationPrepared &&
      isWidgetHeightRecalculationRequired === false &&
      requiredWidgetHeight &&
      noChildIframe
    ) {
      setSynchronisedAnimationPrepared(false);

      activateSynchronisedEnterAnimation({
        widgetResizeDuration,
        mode: {
          appointmentBookingTransitionContainer: showAppointmentBooking,
          cpcCardTransitionContainer: showCpcRecommendations,
          transitionContainer: true,
        },
        isBounceNavigationAnimationEnabled,
      });
    }
  }, [isWidgetHeightRecalculationRequired]);

  return (
    <SwitchTransition>
      <CSSTransition
        in={isIn}
        key={currentPage}
        nodeRef={nodeRef}
        classNames={
          isBounceNavigationAnimationEnabled
            ? 'bounce-direction'
            : 'transition-direction'
        } // "enter-active" or "exit-active" is affixed to the end of the classNames by this component
        timeout={
          widgetResizeDuration
            ? widgetResizeDuration - 100
            : TRANSITION_TIMEOUT_TIME
        } // limits the duration of the enter and/or exit phase
        unmountOnExit
        onExit={handleExitPhase}
        onExited={() => {
          // Inform the wider app of the end of the EXIT phase
          dispatch(
            updateExitPhase({
              exitPhase: false,
            })
          );
        }}
        onEnter={prepareWidgetForEnterAnimation}
        onEntered={() => {
          /**
           * Race condition resolution between the ScrollContainer calculating the scrollArea values (scrollHeight/clientHeight) whilst the inputArea of the next page hasn't fully mounted yet.
           * This callback function announces completion of the mounting phase to the ScrollContainer, thus permitting the recalculation of the scroll area.
           */
          if (!isDynamicHeightEnabled && onContentMounted) {
            onContentMounted();
          }
          if (!window.__private__.isEnterAnimationInProgress) {
            // Inform the wider app of the end of the ENTER phase
            if (
              !window.__private__ ||
              typeof window.__private__ !== 'object' ||
              Array.isArray(window.__private__)
            ) {
              window.__private__ = {};
            }
            if (!window.__private__.isCustomPageTransitionRequired) {
              // isCustomPageTransitionRequired guarantees that isPageTransitionInProgress is 100% accurate at all times
              window.__private__.isPageTransitionInProgress = false;
            }
            dispatch(
              updateEnterPhase({
                enterPhase: false,
              })
            );
          }
        }}
      >
        <div
          ref={nodeRef} // a ref is required to pass the state to the children in strict mode
          style={{
            animationDuration: `${
              widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100
            }ms`, // the animation duration needs to be slightly longer than the timeout of the CSSTransition to allow for the animation to complete
          }}
          className="transitionContainer"
          data-testid="transitionContainer"
        >
          {children}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export default Transition;
